import { useState, useEffect, useContext, useMemo } from 'react'
import useShopifyProduct from '../hooks/useShopifyProduct'
import { LayoutContext } from '../contexts'
import { useFormatPrice } from '../hooks/useFormatPrice'

export const Price = ({
  shopifyProduct,
  selectedVariant,
  lineItem,
  presentmentPrices,
  amount,
  discount,
  compareAtPrice,
}) => {
  const formatPrice = useFormatPrice()
  const { currency: localCurrency, node_locale } = useContext(LayoutContext)

  const [price, setPrice] = useState(null)

  const getSimplePrice = ({ money }) => {
    return (Math.round(money * 100) / 100) * amount
  }

  // --------------------- shopifyProduct as input---------------------------------
  if (shopifyProduct) {
    const { product, loading } = useShopifyProduct(shopifyProduct.handle)
    useEffect(() => {
      if (product && !loading && localCurrency) {
        const currentProduct = selectedVariant
          ? product.variants.edges.find(
              ({ node: { id } }) => selectedVariant === id,
            )
          : product.variants.edges[0]

        const localePrice = currentProduct.node.presentmentPrices.edges.find(
          ({
            node: {
              price: { currencyCode },
            },
          }) => currencyCode === localCurrency.code,
        )
        const resultPrice = compareAtPrice
          ? localePrice?.node?.compareAtPrice.amount
          : localePrice?.node?.price?.amount

        setPrice(parseFloat(resultPrice))
      }
    }, [localCurrency, product, compareAtPrice, selectedVariant])

    if (!price) {
      // if return null, it will show as 0
      // the client only wants to show a number once it's loaded
      return ''
    }

    return discount
      ? formatPrice(price * amount - discount)
      : formatPrice(getSimplePrice({ money: price }))

    // --------------------- lineItem as input---------------------------------
  } else if (lineItem) {
    useEffect(() => {
      if (localCurrency) {
        const localePrice = lineItem.node.variant.presentmentPrices.edges.find(
          ({
            node: {
              price: { currencyCode },
            },
          }) => currencyCode === localCurrency.code,
        )

        const resultPrice = compareAtPrice
          ? localePrice?.node?.compareAtPrice.amount
          : localePrice?.node?.price?.amount

        setPrice(parseFloat(resultPrice))
      }
    }, [localCurrency, lineItem, compareAtPrice])

    return discount
      ? formatPrice(price * amount - discount)
      : formatPrice(getSimplePrice({ money: price }))

    // ----------- presentmentPrices as input (for bundles with multiple prices) ------------
  } else if (presentmentPrices) {
    useEffect(() => {
      if (localCurrency) {
        setPrice(
          presentmentPrices
            .map(({ edges }) =>
              edges.find(
                ({
                  node: {
                    price: { currencyCode },
                  },
                }) => currencyCode === localCurrency.code,
              ),
            )
            .map(
              ({
                node: {
                  price: { amount },
                },
              }) => parseFloat(amount),
            )
            .reduce((acc, cur) => acc + cur, 0),
        )
      }
    }, [localCurrency, presentmentPrices])

    return discount
      ? formatPrice(price - discount)
      : formatPrice(Math.round(price * 100) / 100)
  }

  return null
}

export default Price
